import {
    SearchFilterDefinition,
    SearchFilterOption,
    SearchFilterSelection,
    SearchFilterValue,
} from 'shared/search-engine';

export interface MathiaFacetItem {
    id?: string | number;
    value?: string | number;
    name?: string;
    label?: string;
    count?: string | number;
    [key: string]: unknown;
}

export type MathiaFacetScope = 'activity' | 'lesson';

export interface MathiaSearchFilterConfig {
    key: string;
    queryParam: string;
    labelKey: string;
    facetKey?: string;
    control?: 'select' | 'checkbox';
    independent?: boolean;
    omitWhenFalse?: boolean;
    multiple?: boolean;
}

/**
 * The array order is the display order. The filter bar deliberately does not
 * infer dependencies from the order: the Mathia adapter reloads the facets
 * from the API when a preceding value changes.
 */
export const MATHIA_SEARCH_FILTER_CONFIG: readonly MathiaSearchFilterConfig[] = [
    {
        key: 'level',
        queryParam: 'level',
        labelKey: 'generic.level',
        facetKey: 'levels',
        multiple: true,
    },
    {
        key: 'concepts',
        queryParam: 'concepts',
        labelKey: 'generic.concepts',
        facetKey: 'concepts',
        multiple: true,
    },
    {
        key: 'thematics',
        queryParam: 'thematics',
        labelKey: 'generic.thematics',
        facetKey: 'thematics',
        multiple: true,
    },
    {
        key: 'skills',
        queryParam: 'skills',
        labelKey: 'generic.skills',
        facetKey: 'skills',
        multiple: true,
    },
];

export const MATHIA_PARCOURS_SEARCH_FILTER_CONFIG: readonly MathiaSearchFilterConfig[] = [
    ...MATHIA_SEARCH_FILTER_CONFIG,
    {
        key: 'bookmarks',
        queryParam: 'bookmarks',
        labelKey: 'generic.filter_by_bookmarks',
        control: 'checkbox',
        independent: true,
        omitWhenFalse: true,
    },
];

export type MathiaSearchSelection = SearchFilterSelection<SearchFilterValue>;

export function buildMathiaFilterDefinitions(
    facets: Record<string, unknown>,
    config: readonly MathiaSearchFilterConfig[] = MATHIA_SEARCH_FILTER_CONFIG,
    optionOverrides: Readonly<Record<string, readonly SearchFilterOption<SearchFilterValue>[]>> = {},
): SearchFilterDefinition<SearchFilterValue>[] {
    return config.map(filterConfig => {
        const options = optionOverrides[filterConfig.key]
            ?? (filterConfig.facetKey ? toOptions(facets[filterConfig.facetKey]) : []);

        return {
            key: filterConfig.key,
            queryParam: filterConfig.queryParam,
            labelKey: filterConfig.labelKey,
            options,
            control: filterConfig.control,
            multiple: filterConfig.multiple === true,
            enabled: filterConfig.control === 'checkbox' || options.length > 0,
        } satisfies SearchFilterDefinition<SearchFilterValue>;
    });
}

export function toOptions(rawOptions: unknown): SearchFilterOption<SearchFilterValue>[] {
    if (!Array.isArray(rawOptions)) {
        return [];
    }

    return rawOptions
        .map((rawOption: MathiaFacetItem): SearchFilterOption<SearchFilterValue> | null => {
            const value = rawOption?.id ?? rawOption?.value;
            if (value === undefined || value === null) {
                return null;
            }

            return {
                value,
                label: String(rawOption.name ?? rawOption.label ?? value),
                metadata: rawOption,
            } satisfies SearchFilterOption<SearchFilterValue>;
        })
        .filter((option): option is SearchFilterOption<SearchFilterValue> => option !== null);
}
